import { ListProps, Paging } from '@Core';
import { CameraPhotoType, CameraType, IUpsertCameraProps } from '@EcamModel/controllers/ICameraHttpController';
import { IUpdateLatestPhoto } from '@EcamModel/controllers/ICameraViewHttpController';
import { ISendActionGet } from '@EcamModel/controllers/IPTZCamConfigureRequestHttpController';
import { CameraWithInfo, ExemptionPeriods, GeoZoneCoordinates, GeoZones, Status } from '@EcamModel/model';
import { CameraTrailAuditEvent } from '@EcamModel/model/CameraTrailAudit';
import { CameraView, CameraViewType, PatrolTimeForecast, PhotoSnapShotByPTZ } from '@EcamModel/model/CameraView';
import EventLogIcon from '@assets/jsx-icon/EventLogIcon';
import ICNote from '@assets/jsx-icon/ICNote';
import InformationTabsIcon from '@assets/jsx-icon/InformationTabsIcon';
import TaskIcon from '@assets/jsx-icon/TaskIcon';
import { IBreadCrumbs } from '@components/breadcumbs/BreadCumbs';
import { DrawerContext, DrawerPage } from '@components/drawer/useDrawer';
import { TabConfig } from '@components/tabs/BaseEnhancedTabs';
import { pushError, pushSuccess, pushWarning } from '@components/toast';
import { AxiosError } from '@controllers/axiosClient';
import {
    cameraController,
    cameraTrailAuditEventHttpController,
    cameraViewController,
    exemptionController,
    geoZoneController,
} from '@controllers/index';
import usePopUp from '@hooks/usePopUp';
import useScrollToTop from '@hooks/useScrollToTop';
import { Filter, initPaging } from '@pages/cameras/list';
import { useImageDimensions } from '@pages/cameras/live-photos/components/PreviewImage';
import { OwnFilter } from '@pages/overview/components/CancellationReasons';
import color from '@theme/Colors';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { GiCctvCamera } from 'react-icons/gi';
import { PiCubeFocusThin } from 'react-icons/pi';
import { useQuery, useQueryClient } from 'react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useBackdrop } from 'src/providers/BackdropProvider';
import { useDebounce } from 'usehooks-ts';
import { typeTextMap } from './GeoZonesNavigateHandler';
import BackdatePCNsIcon from '@assets/jsx-icon/BackdatePCNsIcon';
import { IoTrendingUpOutline } from 'react-icons/io5';
import _ from 'lodash';

export const getGeoZone = (_scale: number, prevGeoZonePoints: GeoZoneCoordinates[][]) => {
    return prevGeoZonePoints?.map((polygon) =>
        polygon?.map((point) => ({
            x: point?.XPoint * _scale,
            y: point?.YPoint * _scale,
            geoZoneId: point?.GeoZoneId,
        }))
    );
};

export type HoveredFrame = { index?: number; cameraViewId: number } | null;

export const PREVIEW_RATIO = 0.5625;

export default function useCameraDetailProvider() {
    const params = useParams<{ id: string }>();
    const location = useLocation();
    const state = location?.state;

    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    const cameraViewIdParam = searchParams.get('cameraViewId');

    const cameraViewId: number | undefined =
        state?.cameraViewId || (cameraViewIdParam ? Number(cameraViewIdParam) : undefined);

    const navigate = useNavigate();
    const { open, setActiveItem } = useContext(DrawerContext);

    useScrollToTop();
    useEffect(() => {
        setActiveItem(DrawerPage.Cameras);
    }, []);

    const backdrop = useBackdrop();
    const queryClient = useQueryClient();

    const [camera, setCamera] = useState<CameraWithInfo>({} as CameraWithInfo);
    const [loading, setLoading] = useState<boolean>(true);

    const latestBlobPhoto = camera.CameraPhotos;
    const originalBlobPhoto = camera.OriginalPhoto;
    const notAvailable = camera.Status === Status.NotAvailable;
    const captureAtLatest = camera.CameraPhotos?.[0]?.CaptureAt;
    const captureAtOriginal = camera.OriginalPhoto?.CaptureAt;
    const captureAtLastEdited = camera?.LastEditedPhoto?.CaptureAt;
    const timeZone = camera.TimeZone;

    const containerRef = useRef<HTMLDivElement | null>(null);
    const [containerSize, setContainerSize] = useState({
        width: window.innerWidth,
        height: Math.min(window.innerWidth * PREVIEW_RATIO, 300),
    });

    const [wakeUpLoading, setWakeUpLoading] = useState(false);
    
    const getInitialTabValue = () => {
        if (state?.tabValue !== undefined) return state.tabValue;
        if (tabParam === 'geozone') {
            return 1;
        }
        return 0;
    };

    const [value, setValue] = useState<number>(getInitialTabValue());
    const [openIMGPreview, setOpenIMGPreview] = useState<boolean>(false);
    const [photoSnapShotByPTZ, setPhotoSnapShotByPTZ] = useState<PhotoSnapShotByPTZ>({} as PhotoSnapShotByPTZ);

    const blobLatestPhoto = latestBlobPhoto?.[0]?.BlobName;
    const blobOriginalPhoto = originalBlobPhoto?.BlobName;
    const blobLatestEditedPhoto = camera?.LastEditedPhoto?.BlobName;
    const { image: imageLatest, loading: loadingLatest, isError: isErrorLatest } = useImageDimensions(blobLatestPhoto);
    const {
        image: imageOriginal,
        loading: loadingOriginal,
        isError: isErrorOriginal,
    } = useImageDimensions(blobOriginalPhoto);

    const { image: imageLatestEdited } = useImageDimensions(blobLatestEditedPhoto);

    const widthImage = 1245;

    const scaleNotFullLatestCameraSolar = containerSize.width / (imageLatest?.width ?? 1920);
    const scaleFullLatestCameraSolar = 1280 / (imageLatest?.width ?? 1920);

    const scaleNotFullOriginalCameraSolar = containerSize.width / (imageOriginal?.width ?? 1920);
    const scaleFullOriginalCameraSolar = 1280 / (imageOriginal?.width ?? 1920);

    const scaleNotFullLatestEditedCameraSolar = containerSize.width / (imageLatestEdited?.width ?? 1920);
    const scaleFullLatestEditedCameraSolar = 1280 / (imageLatestEdited?.width ?? 1920);

    const breadcrumbs: IBreadCrumbs[] = [
        { title: 'Camera list', onBack: true },
        { title: `${camera.Name}`, onBack: false, isColorBlackText: true },
    ];

    const [filteredCamera, setFilteredCamera] = useState<ISendActionGet>({} as ISendActionGet);
    const [listCameraView, setListCameraView] = useState<Paging<CameraView>>({ ...initPaging });
    const [selectedCameraView, setSelectedCameraView] = useState<CameraView>();
    const [selectedGeozone, setSelectedGeozone] = useState<GeoZones>();

    const [hoveredFrame, setHoveredFrame] = useState<HoveredFrame>(null);

    const [anchorElMenuLists, setAnchorElMenuLists] = useState<null | { mouseX: number; mouseY: number }>(null);
    const openMenuLists = Boolean(anchorElMenuLists);

    const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);

    const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null);
    const [imageElementSP111, setImageElementSP111] = useState<HTMLImageElement | null>(null);

    const popUpDeleteGeoZonesOverview = usePopUp();

    const scaleNotFullCameraPTZ = containerSize.width / (imageElement?.width ?? 1920);
    const scaleFullCameraPTZ = 1280 / (imageElement?.width ?? 1920);

    const scaleNotFullCameraSP111 = containerSize.width / (imageElementSP111?.width ?? 1920);
    const scaleFullCameraSP111 = 1280 / (imageElementSP111?.width ?? 1920);

    const [filterTrailAuditEvent, setFilterTrailAuditEvent] = useState<Filter>({
        page: 1,
        from: undefined,
        to: undefined,
        search: '',
    });

    const [filterSortStateTrailAuditEvent, setFilterSortStateTrailAuditEvent] = useState<OwnFilter>({
        sorts: ['-Updated'],
    } as OwnFilter);
    const debouncedValue = useDebounce<string>(filterTrailAuditEvent.search ?? '', 500);

    const setPartialTrailAuditEvent = (e: Partial<Filter>) => {
        setFilterTrailAuditEvent((prev) => ({ ...prev, ...e, page: 1 }));
    };

    const getDetailsCamera = async () => {
        setLoading(true);
        await cameraController
            .get(params.id!)
            .then(async (res) => {
                setCamera(res);

                const filteredCamera: ISendActionGet = {
                    ip: res.IP,
                    authenticate: {
                        password: res.PassWord,
                        username: res.UserName,
                    },
                };

                setFilteredCamera(filteredCamera);
            })
            .catch((err) => {
                pushError(err.response?.data?.message);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        getDetailsCamera();
    }, [params.id]);

    useEffect(() => {
        if (camera.Id && tabParam === 'geozone') {
            const hasPTZCamera = !!camera.PTZCameraId;
            const geoZoneTabIndex = hasPTZCamera ? 2 : 1;
            setValue(geoZoneTabIndex);
        }
    }, [camera.Id, camera.PTZCameraId, tabParam]);

    const handleUpdateCamera = async (_camera: CameraWithInfo) => {
        backdrop.setTrue();
        await cameraController
            .upsertWithSolarCamera(_camera as IUpsertCameraProps)
            .then((res) => {
                setCamera(res);
                pushSuccess('Saved successfully');
            })
            .catch((err) => {})
            .finally(() => backdrop.setFalse());
    };

    const handleAddExemption = (exemption: ExemptionPeriods) => {
        return exemptionController
            .upsert(exemption)
            .then(() => {
                return exemptionController
                    .list({
                        filter: {
                            CameraId: camera.Id,
                        },
                    })
                    .then((res) => {
                        setCamera((prev) => ({ ...prev, ExemptionPeriods: res.rows }));
                        pushSuccess('Added successfully');
                        return true;
                    });
            })
            .catch((err) => {
                pushError(err?.response?.data?.message ?? 'Failed to save the exemption period');
                return false;
            });
    };

    const handleDeleteExemption = (exemptionId: number) => {
        return exemptionController
            .delete(exemptionId.toString())
            .then(() => {
                return exemptionController
                    .list({
                        filter: {
                            CameraId: camera.Id,
                        },
                    })
                    .then((res) => {
                        setCamera((prev) => ({ ...prev, ExemptionPeriods: res.rows }));
                        pushSuccess('Deleted successfully');
                        return true;
                    });
            })
            .catch((err) => {
                pushError(err?.response?.data?.message ?? 'Failed to delete the exemption period');
                return false;
            });
    };

    const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    const hasPTZCamera = !!camera.PTZCameraId;
    const getTabIndex = (baseIndex: number) => (hasPTZCamera ? baseIndex : baseIndex - 1);

    const tabs: TabConfig[] = [
        {
            label: 'Base information',
            icon: <InformationTabsIcon color={value === 0 ? color.success : '#85858A'} />,
            index: 1,
        },
        ...(camera.PTZCameraId
            ? [
                  {
                      label: 'List preset',
                      icon: (
                          <GiCctvCamera
                              color={value === 1 ? color.success : '#85858A'}
                              fontSize={20}
                              stroke={color.grey600}
                          />
                      ),
                      index: 2,
                  },
              ]
            : []),
        {
            label: `Geo-zones ${
                camera.VendorCameraId !== null || camera.PMC8266CameraId !== null
                    ? `(${camera.GeoZones?.length ?? 0})`
                    : ''
            }`,
            icon: <PiCubeFocusThin color={value === getTabIndex(2) ? color.success : '#85858A'} size={22} />,
            sx: {
                '.MuiTab-iconWrapper': {
                    mb: '2px',
                },
            },
            index: 3,
        },
        {
            label: 'More information',
            icon: <ICNote color={value === getTabIndex(3) ? color.success : '#85858A'} width={17} height={17} />,
            sx: {
                '.MuiTab-iconWrapper': {
                    mb: '2px',
                },
            },
            index: 4,
        },
        {
            label: 'Task',
            icon: <TaskIcon color={value === getTabIndex(4) ? color.success : '#85858A'} width={20} />,
            index: 5,
        },
        {
            label: 'Event log',
            icon: <EventLogIcon color={value === getTabIndex(5) ? color.success : '#85858A'} width={18} height={17} />,
            index: 6,
        },

        ...(camera?.Zone?.IsReadyToGenPotentialContravention && camera?.IsVerificationsEnabled
            ? [
                  {
                      label: 'Backdate PCN',
                      icon: (
                          <BackdatePCNsIcon
                              color={value === getTabIndex(6) ? color.success : '#85858A'}
                              width={18}
                              height={17}
                          />
                      ),
                      index: 8,
                  },
              ]
            : [
                  {
                      label: 'Potential PCN',
                      icon: (
                          <IoTrendingUpOutline
                              color={value === getTabIndex(6) ? color.success : '#85858A'}
                              fontSize={20}
                          />
                      ),
                      index: 8,
                  },
              ]),
    ];

    const prevGeoZonePointsCameraSolarAndSP111: GeoZoneCoordinates[][] =
        (camera.GeoZones?.map((gz) => {
            return gz.GeoZoneCoordinates?.sort((a, b) => a.Index - b.Index).concat(gz.GeoZoneCoordinates[0]);
        }) as GeoZoneCoordinates[][]) ?? [];

    const prevGeoZonesCameraSolar = getGeoZone(scaleFullLatestCameraSolar, prevGeoZonePointsCameraSolarAndSP111);
    const scaledPrevGeoZoneCameraSolar = getGeoZone(
        scaleNotFullLatestCameraSolar,
        prevGeoZonePointsCameraSolarAndSP111
    );

    const prevGeoZonesCameraSP111 = getGeoZone(scaleFullCameraSP111, prevGeoZonePointsCameraSolarAndSP111);
    const scaledPrevGeoZoneCameraSP111 = getGeoZone(scaleNotFullCameraSP111, prevGeoZonePointsCameraSolarAndSP111);

    const handleClickImagePreview = () => {
        setOpenIMGPreview(true);
    };

    const handleRefreshImage = async () => {
        try {
            const res = await (_.isNil(camera.VendorCameraId)
                ? cameraController.getLatestCameraPhotoPMC8266(camera.Id!)
                : cameraController.getLatestCameraPhoto(camera.Id!));

            await refetchTrailAuditEvent();
            const currentPhotoId = camera.CameraPhotos?.[0]?.Id;

            if (!res || res.Id === currentPhotoId) {
                pushWarning("Didn't receive new photos from camera. Please try again");
                return;
            }

            setCamera((prev) => ({ ...prev, CameraPhotos: [res] }));
            pushSuccess('Latest photo has been updated');
        } catch (err: any) {
            console.log({ err });
            pushError(err?.response?.data?.message);
        }
    };

    const handleDeleteGeoZoneSuccess = (id: string, isGzOverView: boolean = true) => {
        popUpDeleteGeoZonesOverview.onClose();
        pushSuccess('Delete geo-zone successfully');

        setListCameraView((prevList) => {
            const idToRemove = Number(id);

            const rows = prevList.rows.map((view) => ({
                ...view,
                GeoZones: (view.GeoZones || [])
                    .map((gz) => ({
                        ...gz,
                        CameraViewDetail: gz.CameraViewDetail
                            ? {
                                  ...gz.CameraViewDetail,
                                  GeoZones: (gz.CameraViewDetail.GeoZones || []).filter((z) => z.Id !== idToRemove),
                              }
                            : gz.CameraViewDetail,
                    }))
                    .filter((gz) => gz.Id !== idToRemove),
            }));

            return { ...prevList, rows };
        });

        setSelectedCameraView((prev) => ({ ...prev!, GeoZones: prev?.GeoZones?.filter((gz) => gz.Id !== Number(id)) }));
        isGzOverView && setSelectedGeozone(undefined);
    };

    const handleDeleteGeoZonesOverView = async (id: string) => {
        setIsLoadingSave(true);
        await geoZoneController
            .deleteGeoZoneOverview(Number(id))
            .then(async (_) => {
                handleDeleteGeoZoneSuccess(id);
                queryClient.invalidateQueries(['trailAuditEvent']);
                queryClient.invalidateQueries(['patrolTimeForecast']);
            })
            .catch((err) => {
                pushError(err.response?.data?.message);
            })
            .finally(() => {
                setIsLoadingSave(false);
                setAnchorElMenuLists(null);
            });
    };

    const handleDeleteGeoZonesDetail = async (id: string) => {
        setIsLoadingSave(true);
        await geoZoneController
            .delete(id)
            .then((_) => {
                handleDeleteGeoZoneSuccess(id, false);
                queryClient.invalidateQueries(['trailAuditEvent']);
                queryClient.invalidateQueries(['patrolTimeForecast']);
            })
            .catch((err) => {
                pushError(err.response?.data?.message);
            })
            .finally(() => {
                setIsLoadingSave(false);
                setAnchorElMenuLists(null);
            });
    };

    const [isLoading, setIsLoading] = useState<boolean>(true);

    const handleGetListGeoZoneByView = async (_filter: ListProps<CameraView>) => {
        setIsLoading(true);
        await cameraViewController
            .list(_filter)
            .then((res) => {
                setListCameraView(res);
                if (!selectedCameraView) {
                    setSelectedCameraView(cameraViewId ? findCameraViewById(res.rows, cameraViewId) : res?.rows?.[0]);
                    setSelectedGeozone(
                        cameraViewId ? findGeoZoneByCameraViewDetailId(res.rows, cameraViewId) : undefined
                    );
                }
            })
            .catch((err) => {
                pushError(err.response?.data?.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    function findCameraViewById(data: CameraView[], cameraViewId: number): CameraView | undefined {
        const directMatch = data.find((v) => v.Id === cameraViewId);
        if (directMatch) return directMatch;

        for (const cameraView of data) {
            const geoZoneMatch = cameraView.GeoZones?.find((gz) => gz.CameraViewDetail?.Id === cameraViewId);
            if (geoZoneMatch) return geoZoneMatch.CameraViewDetail;
        }

        return undefined;
    }

    function findGeoZoneByCameraViewDetailId(data: CameraView[], cameraViewId: number): GeoZones | undefined {
        for (const cameraView of data) {
            const geoZoneMatch = cameraView.GeoZones?.find((gz) => gz.CameraViewDetail?.Id === cameraViewId);
            if (geoZoneMatch) return geoZoneMatch;
        }
        return undefined;
    }

    useEffect(() => {
        handleGetListGeoZoneByView({
            pageSize: 10000,
            filter: {
                CameraId: Number(params.id),
                ViewType: CameraViewType.ViewOverview,
            },
            sorts: ['Order', '-Created'],
        });
    }, []);

    const handleCloseMenuLists = () => {
        setAnchorElMenuLists(null);
    };

    const isPointInPolygon = (point: { x: number; y: number }, polygon: { x: number; y: number }[]) => {
        let isInside = false;
        const { x, y } = point;

        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
            const xi = polygon[i].x,
                yi = polygon[i].y;
            const xj = polygon[j].x,
                yj = polygon[j].y;

            const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
            if (intersect) isInside = !isInside;
        }

        return isInside;
    };

    const handleMouseMove = (
        event: any,
        prevGeoZones: {
            x: number;
            y: number;
            geoZoneId: number | undefined;
        }[][],
        cameraViewId: number
    ) => {
        const mousePos = {
            x: event.evt.layerX,
            y: event.evt.layerY,
        };

        const hovered = prevGeoZones.findIndex((frame) => isPointInPolygon(mousePos, frame));

        setHoveredFrame({ index: hovered !== -1 ? hovered : undefined, cameraViewId: cameraViewId });

        if (hovered !== -1) {
            setAnchorElMenuLists({ mouseX: event.evt.clientX, mouseY: event.evt.clientY });
        } else {
            setAnchorElMenuLists(null);
        }
    };

    const handleResetStyledMenu = () => {
        handleCloseMenuLists();
        setHoveredFrame(null);
    };

    const handleUpsertView = async (filter: Filter, viewType: CameraViewType, viewId?: number) => {
        setIsLoadingSave(true);

        const _filter: CameraView = {
            Id: viewId ?? undefined,
            CameraId: camera.Id!,
            CameraViewName: filter?.perspectiveView?.PresetName || '',
            PtzPanPos: photoSnapShotByPTZ.PTZ?.PtzPanPos!,
            PtzZoomPos: photoSnapShotByPTZ.PTZ?.PtzZoomPos!,
            PtzTiltPos: photoSnapShotByPTZ.PTZ?.PtzTiltPos!,
            ViewType: viewType,
            Order: listCameraView?.rows?.length > 0 ? listCameraView.rows.length + 1 : 0,
            PresetId: filter?.perspectiveView?.GotoPointId,
        };

        await cameraViewController
            .upsert(_filter)
            .then(async (res) => {
                await handleUpdateCameraViewPhoto({
                    CameraView: res,
                    PTZCommand: {
                        authenticate: {
                            password: camera.PassWord,
                            username: camera.UserName,
                        },
                        ip: camera.IP,
                    },
                }).then(async (res) => {
                    pushSuccess('Config camera view successfully');
                    queryClient.invalidateQueries(['patrolTimeForecast']);

                    if (!filter?.geozones?.id && !viewId) {
                        navigate(`add/${typeTextMap[CameraType.PTZ]}`, {
                            state: {
                                cameraView: {
                                    ...res,
                                    CameraViewName: filter?.perspectiveView?.PresetName,
                                    LastPhotoBlob: `${res.LastPhotoBlob}?${res.Updated}`,
                                    PresetId: res.PresetId,
                                },
                            },
                            replace: true,
                        });

                        return;
                    }

                    if (filter?.geozones?.id) {
                        await handleUpsertGeoZones({
                            Id: Number(filter.geozones.id),
                            CameraViewZoomId: res.Id,
                            CameraId: camera.Id!,
                        });
                    } else if (viewId) {
                        const updatedRows: CameraView[] = listCameraView.rows.map((view) => {
                            if (view.Id === res.Id) {
                                return {
                                    ...view,
                                    CameraViewName: _filter.CameraViewName,
                                    UpdatedBy: res.UpdatedBy,
                                    Updated: res.Updated,
                                    PresetId: res.PresetId,
                                };
                            }

                            if (!view?.GeoZones) return view;

                            return {
                                ...view,
                                GeoZones: view.GeoZones.map((gz) => {
                                    if (gz?.CameraViewDetail?.Id === res.Id) {
                                        return {
                                            ...gz,
                                            CameraViewDetail: {
                                                ...(gz?.CameraViewDetail as any),
                                                CameraViewName: _filter.CameraViewName,
                                                UpdatedBy: res.UpdatedBy,
                                                Updated: res.Updated,
                                                PresetId: res.PresetId,
                                            },
                                        };
                                    }
                                    return gz;
                                }),
                            };
                        });
                        setListCameraView({
                            ...listCameraView,
                            rows: updatedRows,
                        });
                    } else {
                        setListCameraView((prev) => ({ ...prev, rows: [...prev.rows, res] }));
                    }

                    setSelectedCameraView((prev) => ({
                        ...prev!,
                        ...res!,
                        CameraViewName: filter?.perspectiveView?.PresetName,
                        LastPhotoBlob: `${res.LastPhotoBlob}?${res.Updated}`,
                        GeoZones: prev?.GeoZones || res.GeoZones || [],
                    }));
                });
            })
            .finally(() => {
                setIsLoadingSave(false);
            });
    };

    const handleUpsertGeoZones = async (geoZones: GeoZones) => {
        await geoZoneController.upsertGeoZoneOverview(geoZones).then((res) => {
            setSelectedGeozone(res);
            setListCameraView((prev) => ({
                ...prev,
                rows: prev.rows.map((cameraView) => {
                    if (cameraView.Id !== selectedCameraView?.Id) return cameraView;

                    const existed = cameraView.GeoZones?.some((gz) => gz.Id === res.Id);

                    return {
                        ...cameraView,
                        GeoZones: existed
                            ? cameraView.GeoZones?.map((gz) => (gz.Id === res.Id ? res : gz))
                            : [...(cameraView.GeoZones || []), res],
                    };
                }),
            }));
            queryClient.invalidateQueries(['patrolTimeForecast']);
        });
    };

    const refreshCameraView = (res: CameraView) => {
        setSelectedCameraView((prev) => ({
            ...prev!,
            Id: res.Id,
            CameraViewName: res.CameraViewName,
            Updated: res.Updated,
            UpdatedBy: res.UpdatedBy,
            LastPhotoBlob: `${res.LastPhotoBlob}?${res.Updated}`,
            GeoZones: prev?.GeoZones || res.GeoZones || [],
        }));
    };

    const handleDeleteCameraOverview = async (id: number) => {
        await cameraViewController.delete(id.toString()).then(() => {
            setListCameraView((prev) => {
                const updatedCameraViews = prev.rows?.filter((view) => view.Id !== id) ?? [];

                setSelectedCameraView(updatedCameraViews.length > 0 ? updatedCameraViews[0] : undefined);

                return {
                    ...prev,
                    rows: updatedCameraViews,
                };
            });
            queryClient.invalidateQueries(['patrolTimeForecast']);
        });
    };

    const handleSetOriginalPhoto = async (photoType: CameraPhotoType, cameraPhotoId?: number) => {
        backdrop.setTrue();
        await cameraController
            .setCameraPhoto({
                CameraId: camera.Id!,
                CameraPhotoId: cameraPhotoId ?? latestBlobPhoto?.[0].Id!,
                PhotoType: photoType,
            })
            .then(async (res) => {
                await refetchTrailAuditEvent();
                pushSuccess('Original photo has been updated');
                setCamera((prev) => ({
                    ...prev,
                    [photoType === CameraPhotoType.Original ? 'OriginalPhoto' : 'LastEditedPhoto']: res,
                }));
            })
            .catch(() => {})
            .finally(() => {
                backdrop.setFalse();
            });
    };

    const getTrailAuditEvent = async (): Promise<Paging<CameraTrailAuditEvent>> => {
        const response = await cameraTrailAuditEventHttpController.list({
            page: filterTrailAuditEvent.page ?? 1,
            From: filterTrailAuditEvent.from ?? undefined,
            To: filterTrailAuditEvent.to ?? undefined,
            pageSize: 10,
            search: {
                content: filterTrailAuditEvent.search ?? '',
                fields: ['UpdatedBy'],
            },
            filter: {
                CameraId: Number(params.id)!,
                AuditEventType: filterTrailAuditEvent.eventLog?.Value,
                EntityName: filterTrailAuditEvent.entityName?.Value,
            },
            sorts: filterSortStateTrailAuditEvent.sorts as any,
        });
        return response;
    };

    const handleDndViewOverview = async (newRows: CameraView[]) => {
        await cameraViewController
            .updateOrder({
                list: newRows.map((e, i) => ({
                    CameraViewId: e.Id!,
                    Order: i,
                })),
            })
            .then(() => {
                pushSuccess('Update camera overview successfully');
            })
            .finally(() => setIsLoading(false));
    };

    const handleUpdateCameraViewPhoto = async (data: IUpdateLatestPhoto) => {
        return await cameraViewController.updateLatestPhoto(data);
    };

    const {
        data: trailAuditEvent,
        error,
        isLoading: isLoadingTrailAuditEvent,
        isFetching,
        refetch: refetchTrailAuditEvent,
    } = useQuery<Paging<CameraTrailAuditEvent>, AxiosError>(
        [
            'trailAuditEvent',
            params.id,
            filterTrailAuditEvent.page,
            filterTrailAuditEvent.from,
            filterTrailAuditEvent.to,
            filterTrailAuditEvent.eventLog,
            filterTrailAuditEvent.entityName,
            filterSortStateTrailAuditEvent.sorts,
            debouncedValue,
        ],
        getTrailAuditEvent,
        {
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            onError: (err) => {
                pushError(err?.message);
            },
        }
    );

    const getPatrolTimeForecast = async (): Promise<PatrolTimeForecast> => {
        if (!Number(params.id)) return {} as PatrolTimeForecast;
        const response = await cameraViewController.getPatrolTimeForecast({ CameraId: Number(params.id) });
        return response;
    };

    const {
        data: patrolTimeForecast,
        isLoading: isLoadingPatrolTimeForecast,
        isFetching: isFetchingPatrolTimeForecast,
    } = useQuery<PatrolTimeForecast, AxiosError>(['patrolTimeForecast', params.id], getPatrolTimeForecast, {
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        onError: (err) => {
            pushError(err?.message);
        },
    });

    useEffect(() => {
        const updateStageSize = () => {
            if (containerRef.current) {
                const width = containerRef.current.offsetWidth;
                const height = width * PREVIEW_RATIO;
                setContainerSize({ width, height });
            } else {
                setTimeout(updateStageSize, 300);
            }
        };

        if (open) {
            setTimeout(updateStageSize, 300);
        } else {
            updateStageSize();
        }
        window.addEventListener('resize', updateStageSize);

        return () => {
            window.removeEventListener('resize', updateStageSize);
        };
    }, [open]);

    return {
        isLoading,
        isFetching,
        isLoadingTrailAuditEvent,
        trailAuditEvent,
        breadcrumbs,
        navigate,
        camera,
        setCamera,
        value,
        handleChangeTabs,
        loading,
        setLoading,
        isLoadingSave,
        setIsLoadingSave,
        wakeUpLoading,
        setWakeUpLoading,
        openIMGPreview,
        setOpenIMGPreview,
        hoveredFrame,
        setHoveredFrame,
        containerSize,
        setContainerSize,
        anchorElMenuLists,
        imageElement,
        setImageElement,
        imageElementSP111,
        setImageElementSP111,
        filteredCamera,
        photoSnapShotByPTZ,
        setPhotoSnapShotByPTZ,
        listCameraView,
        setListCameraView,
        filterTrailAuditEvent,
        setFilterTrailAuditEvent,
        filterSortStateTrailAuditEvent,
        setFilterSortStateTrailAuditEvent,

        imageLatest,
        imageOriginal,
        blobLatestPhoto,
        blobOriginalPhoto,
        imageLatestEdited,
        blobLatestEditedPhoto,
        notAvailable,
        tabs,
        prevGeoZonesCameraSolar,
        scaleNotFullLatestCameraSolar,
        scaleFullLatestCameraSolar,
        scaledPrevGeoZoneCameraSolar,
        scaleNotFullOriginalCameraSolar,
        scaleFullOriginalCameraSolar,
        scaleNotFullCameraPTZ,
        scaleFullCameraPTZ,
        scaleNotFullLatestEditedCameraSolar,
        scaleFullLatestEditedCameraSolar,
        scaleNotFullCameraSP111,
        scaleFullCameraSP111,
        prevGeoZonesCameraSP111,
        scaledPrevGeoZoneCameraSP111,
        widthImage,
        timeZone,
        captureAtOriginal,
        captureAtLatest,
        captureAtLastEdited,
        openMenuLists,
        containerRef,
        patrolTimeForecast,
        isLoadingPatrolTimeForecast,
        isFetchingPatrolTimeForecast,

        refetchTrailAuditEvent,
        handleAddExemption,
        handleDeleteExemption,
        handleUpdateCamera,
        handleClickImagePreview,
        handleRefreshImage,
        handleDeleteGeoZonesOverView,
        handleDeleteGeoZonesDetail,
        handleMouseMove,
        handleResetStyledMenu,
        handleUpsertView,
        handleSetOriginalPhoto,
        setAnchorElMenuLists,
        setPartialTrailAuditEvent,

        popUpDeleteGeoZonesOverview,

        selectedCameraView,
        setSelectedCameraView,
        selectedGeozone,
        setSelectedGeozone,

        backdrop,
        handleUpdateCameraViewPhoto,
        refreshCameraView,
        handleDeleteCameraOverview,

        getDetailsCamera,
        handleDndViewOverview,

        // setSystemTime,
        // enableValueInContinuousCapture,
        // cellularSettings,
        // setStatus,
        // setSettingsExpected,
        // systemTimeMilesight,
    };
}

type CameraDetailProviderContextType = ReturnType<typeof useCameraDetailProvider>;

export const CameraDetailContext = createContext<CameraDetailProviderContextType>(
    {} as CameraDetailProviderContextType
);

export const useCameraDetailContext = () => useContext(CameraDetailContext);
